import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "../api/axios";
import { PreferencesContext } from "../context/PreferencesContext";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Autocomplete,
  Stack,
  Divider,
} from "@mui/material";
import PartialDeliveryDialog from "../components/PartialDeliveryDialog";
import { useReactToPrint } from "react-to-print";
import SaleReceipt from "../components/SaleReceipt";
import CommonTable from "../components/CommonTable";
import { useMessage } from "../context/MessageContext";
import { useConfirm } from "../context/ConfirmContext";
import { AuthContext } from "../context/AuthContext";
import { formatSaleDisplayId } from "../utils/saleHelpers";
import { GENDERS, variantLabel, onlyNonEmptyStrings, safeDisplay, toNumber } from "../utils/uniformConstants";
import { displayText, itemCatalogLabel } from "../utils/catalogOptionUtils";

const paymentModes = ["Cash", "UPI", "Bank Transfer", "Card"];

const UniformSales = () => {
  const { organization, academicYear } =
    useContext(PreferencesContext);
  const { user } = useContext(AuthContext);
  const { showMessage } = useMessage();
  const { confirm } = useConfirm();

  const [branch, setBranch] = useState("");
  const [branches, setBranches] = useState([]);
  const [sales, setSales] = useState([]);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);

  const defaultForm = {
    studentId: "",
    studentName: "",
    grade: "",
    program: "",
    paymentMode: "",
    paymentInfo: "",
    manualReceiptNumber: "",
    discountVouchers: [],
    saleDate: new Date(),
    roundOff: 0,
  };
  const [form, setForm] = useState(defaultForm);

  const [studentInput, setStudentInput] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentOptions, setStudentOptions] = useState([]);

  const [picker, setPicker] = useState({
    gender: "",
    uniformGroup: "",
    uniformVariant: "",
    size: "",
  });
  const [filterOptions, setFilterOptions] = useState({
    groups: [],
    variants: [],
    sizes: [],
  });
  const [matchedSkus, setMatchedSkus] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);

  const [splitPayment, setSplitPayment] = useState(false);
  const [paymentSplits, setPaymentSplits] = useState([
    { mode: "", amount: "", info: "" },
  ]);

  const [deliverDialogOpen, setDeliverDialogOpen] = useState(false);
  const [deliverSale, setDeliverSale] = useState(null);
  const [sourceInventory, setSourceInventory] = useState([]);

  const receiptRef = useRef();
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [receiptSale, setReceiptSale] = useState(null);

  const getVisibleBranches = () =>
    user.role === "branch_manager"
      ? branches.filter((b) =>
          user.branches.some((ub) => String(ub) === String(b._id))
        )
      : branches;

  const getBranchName = (branchId) =>
    branches.find((b) => b._id === branchId)?.name || "";

  const stockMap = useMemo(() => {
    const map = {};
    sourceInventory.forEach((inv) => {
      map[String(inv.item)] = toNumber(inv.saleableQuantity ?? inv.quantity);
    });
    return map;
  }, [sourceInventory]);

  const getSkuStock = (sku) => {
    const fromApi = sku?.stock;
    if (typeof fromApi === "number" && !Number.isNaN(fromApi)) return fromApi;
    const id = displayText(sku?._id, "");
    if (id && stockMap[id] != null) return stockMap[id];
    return toNumber(fromApi);
  };

  useEffect(() => {
    if (!organization || !branch) return;
    axios
      .get("/inventory/balance", {
        params: { organization, academicYear, branch },
      })
      .then((res) => setSourceInventory(res.data || []));
  }, [organization, academicYear, branch]);

  useEffect(() => {
    if (!organization) return;
    axios.get(`/branches?orgId=${organization}`).then((res) => {
      setBranches(res.data || []);
    });
  }, [organization]);

  useEffect(() => {
    const visible = getVisibleBranches();
    if (visible.length === 1) setBranch(String(visible[0]._id));
  }, [branches, user]);

  useEffect(() => {
    if (branch && organization && academicYear) fetchSales();
  }, [branch, organization, academicYear]);

  useEffect(() => {
    if (studentInput.length < 2 || !branch) return;
    axios
      .get(
        `/students?orgId=${organization}&academicYear=${academicYear}&branch=${branch}&q=${studentInput}`
      )
      .then((res) => setStudentOptions(res.data || []));
  }, [studentInput, organization, academicYear, branch]);

  useEffect(() => {
    setStudentOptions([]);
  }, [organization, academicYear, branch]);

  useEffect(() => {
    if (!open) {
      setForm(defaultForm);
      setSelectedStudent(null);
      setStudentInput("");
      setSelectedItems([]);
      setPicker({ gender: "", uniformGroup: "", uniformVariant: "", size: "" });
      setStep(1);
      setSplitPayment(false);
      setPaymentSplits([{ mode: "", amount: "", info: "" }]);
    }
  }, [open]);

  const fetchSales = async () => {
    if (!branch || !organization || !academicYear) {
      return;
    }
    try {
      const res = await axios.get("/sales", {
        params: {
          orgId: organization,
          academicYear,
          branch,
          saleType: "uniforms",
        },
      });
      setSales(res.data || []);
    } catch (err) {
      showMessage(
        err.response?.data?.msg || "Failed to load uniform sales",
        "error"
      );
    }
  };

  const enrichSaleForReceipt = (saved, cartItems) => {
    const items = (saved.items || []).map((line) => {
      const lineItemId =
        line.item?._id?.toString?.() || line.item?.toString?.() || "";
      const name =
        line.item?.name ||
        cartItems.find((c) => c.id === lineItemId)?.label ||
        "";
      return {
        ...line,
        item:
          line.item && typeof line.item === "object"
            ? { ...line.item, name }
            : { _id: lineItemId, name },
      };
    });
    return {
      ...saved,
      items,
      receiptNo: formatSaleDisplayId(saved.saleNumber),
      receiptTitle: "Uniform Sale Receipt",
    };
  };

  const openReceiptForSale = (saved, cartItems = []) => {
    setReceiptSale(enrichSaleForReceipt(saved, cartItems));
    setReceiptDialogOpen(true);
  };

  const loadFilterOptions = async (nextPicker = picker) => {
    if (!organization || !academicYear) return;
    const res = await axios.get("/items/uniform-catalog/filters", {
      params: {
        organization,
        academicYear,
        branch,
        grade: selectedStudent?.grade,
        gender: nextPicker.gender || undefined,
        uniformGroup: nextPicker.uniformGroup || undefined,
        uniformVariant: nextPicker.uniformVariant || undefined,
      },
    });
    setFilterOptions({
      groups: onlyNonEmptyStrings(res.data.groups),
      variants: onlyNonEmptyStrings(res.data.variants),
      sizes: onlyNonEmptyStrings(res.data.sizes),
    });
  };

  useEffect(() => {
    if (!selectedStudent || !open) return;
    loadFilterOptions();
  }, [selectedStudent, open, branch, organization, academicYear]);

  useEffect(() => {
    if (!selectedStudent || !picker.gender || !open) {
      setMatchedSkus([]);
      return;
    }

    const fetchSkus = async () => {
      const res = await axios.get("/items/uniform-catalog", {
        params: {
          organization,
          academicYear,
          branch,
          grade: selectedStudent.grade,
          gender: picker.gender,
          uniformGroup: picker.uniformGroup || undefined,
          uniformVariant: picker.uniformVariant || undefined,
          size: picker.size || undefined,
        },
      });
      setMatchedSkus(res.data || []);
    };
    fetchSkus();
  }, [picker, selectedStudent, open, branch, organization, academicYear]);

  const handlePickerChange = async (field, value) => {
    const normalized = typeof value === "string" ? value : "";
    const next = { ...picker, [field]: normalized };
    if (field === "gender") {
      next.uniformGroup = "";
      next.uniformVariant = "";
      next.size = "";
    } else if (field === "uniformGroup") {
      next.uniformVariant = "";
      next.size = "";
    } else if (field === "uniformVariant") {
      next.size = "";
    }
    setPicker(next);
    await loadFilterOptions(next);
  };

  const addSkuToCart = (sku) => {
    const id = displayText(sku._id, "");
    if (!id) return showMessage("Invalid item", "error");
    if (selectedItems.some((it) => it.id === id)) {
      return showMessage("Item already in cart", "warning");
    }
    setSelectedItems((list) => [
      ...list,
      {
        id,
        label: itemCatalogLabel(sku),
        price: toNumber(sku.price),
        quantity: 1,
        stock: getSkuStock(sku),
        isCollection: false,
      },
    ]);
  };

  const getTotalAmount = (items) => {
    const rawTotal = items.reduce(
      (sum, item) => sum + item.quantity * item.price,
      0
    );
    const totalDiscount = (form.discountVouchers || []).reduce(
      (sum, v) => sum + (parseFloat(v.amount) || 0),
      0
    );
    return rawTotal - totalDiscount + (parseFloat(form.roundOff) || 0);
  };

  const handleSave = async () => {
    if (!selectedStudent) {
      return showMessage("Please select a student", "warning");
    }
    if (selectedItems.length === 0) {
      return showMessage("Please add at least one uniform item", "warning");
    }

    const totalAmount = getTotalAmount(selectedItems);
    let paymentsPayload;

    if (splitPayment) {
      const validPayments = paymentSplits.filter(
        (p) => p.mode && parseFloat(p.amount) > 0
      );
      if (!validPayments.length) {
        return showMessage("Add at least one payment with mode and amount", "warning");
      }
      const paymentSum = validPayments.reduce(
        (sum, p) => sum + parseFloat(p.amount),
        0
      );
      if (Math.abs(paymentSum - totalAmount) > 0.01) {
        return showMessage("Payment total must equal sale total", "error");
      }
      paymentsPayload = validPayments.map((p) => ({
        mode: p.mode,
        amount: parseFloat(p.amount),
        info: p.info || "",
      }));
    } else if (!form.paymentMode) {
      return showMessage("Please select a payment mode", "warning");
    }

    const payload = {
      ...form,
      organization,
      academicYear,
      branch,
      student: selectedStudent._id,
      saleType: "uniforms",
      items: selectedItems.map((it) => ({
        item: it.id,
        isCollection: false,
        quantity: it.quantity,
        price: it.price,
      })),
      totalAmount,
    };

    if (splitPayment) {
      payload.payments = paymentsPayload;
      delete payload.paymentMode;
      delete payload.paymentInfo;
    }

    try {
      const res = await axios.post("/sales", payload);
      const saved = res.data;
      const cartSnapshot = [...selectedItems];
      setOpen(false);
      setSelectedItems([]);
      await fetchSales();
      openReceiptForSale(saved, cartSnapshot);
      showMessage("Uniform sale saved", "success");
    } catch (err) {
      showMessage(err.response?.data?.msg || "Failed to save sale", "error");
    }
  };

  const handleDeliver = async (deliveryItems) => {
    await axios.post(`/sales/${deliverSale._id}/deliver`, {
      items: deliveryItems,
    });
    setDeliverDialogOpen(false);
    setDeliverSale(null);
    fetchSales();
  };

  const handleCancelSale = async (sale) => {
    const { confirmed, reason } = await confirm({
      title: "Cancel uniform sale",
      message: `Cancel sale ${formatSaleDisplayId(sale.saleNumber)}?`,
      requireReason: true,
    });
    if (!confirmed) return;
    try {
      await axios.post(`/sales/${sale._id}/revert`, { reason });
      showMessage("Sale cancelled", "success");
      fetchSales();
    } catch (err) {
      showMessage(err.response?.data?.msg || "Failed to cancel sale", "error");
    }
  };

  const handlePrintReceipt = (sale) => {
    openReceiptForSale(sale);
  };

  const handlePrint = useReactToPrint({
    content: () => receiptRef.current,
    removeAfterPrint: true,
  });

  const sizeGridRows = useMemo(() => {
    if (!picker.uniformGroup || !picker.uniformVariant) return [];
    const group =
      typeof picker.uniformGroup === "string" ? picker.uniformGroup : "";
    const variant =
      typeof picker.uniformVariant === "string" ? picker.uniformVariant : "";
    return matchedSkus.filter(
      (sku) =>
        sku.uniformGroup === group && sku.uniformVariant === variant
    );
  }, [matchedSkus, picker.uniformGroup, picker.uniformVariant]);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Uniform Sales
      </Typography>

      <FormControl sx={{ my: 2, minWidth: 250 }}>
        <InputLabel>Branch</InputLabel>
        <Select
          value={branch ? String(branch) : ""}
          onChange={(e) => setBranch(e.target.value)}
          label="Branch"
        >
          {getVisibleBranches().map((b) => (
            <MenuItem key={b._id} value={String(b._id)}>
              {b.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        variant="contained"
        sx={{ my: 2, ml: 2 }}
        onClick={() => {
          if (!branch) return showMessage("Select a branch first", "warning");
          setOpen(true);
        }}
      >
        New Uniform Sale
      </Button>

      <CommonTable
        downloadFileName="Uniform_Sales_Report"
        rows={sales}
        columns={[
          {
            label: "Sale ID",
            renderCell: (s) => formatSaleDisplayId(s.saleNumber),
          },
          {
            label: "Date",
            renderCell: (s) =>
              s.saleDate ? new Date(s.saleDate).toLocaleString("en-IN") : "-",
          },
          {
            label: "Student",
            renderCell: (s) =>
              `${s.studentName} (${s.studentId}, ${s.grade}, ${s.program})`,
          },
          {
            label: "Items",
            flex: true,
            renderCell: (s) =>
              (s.items || [])
                .map((it) => {
                  const name = it.isCollection
                    ? it.collection?.name
                    : it.item?.name;
                  return `${name || "Item"} (${it.quantity})`;
                })
                .join("\n"),
          },
          {
            label: "Total",
            renderCell: (s) => `₹${(s.totalAmount || 0).toLocaleString()}`,
          },
          {
            label: "Status",
            renderCell: (s) => (
              <Chip
                label={
                  s.deliveryStatus === "Created"
                    ? "To Deliver"
                    : s.deliveryStatus
                }
                color={
                  s.deliveryStatus === "Delivered"
                    ? "success"
                    : s.deliveryStatus === "Cancelled"
                    ? "error"
                    : "default"
                }
                size="small"
              />
            ),
          },
          {
            label: "Actions",
            renderCell: (sale) => (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {sale.deliveryStatus !== "Cancelled" && (
                  <>
                    <Button size="small" onClick={() => handlePrintReceipt(sale)}>
                      Receipt
                    </Button>
                    {sale.deliveryStatus !== "Delivered" && (
                      <Button
                        size="small"
                        onClick={() => {
                          setDeliverSale(sale);
                          setDeliverDialogOpen(true);
                        }}
                      >
                        Deliver
                      </Button>
                    )}
                  </>
                )}
                {(user.role === "admin" || user.role === "super_admin") &&
                  sale.deliveryStatus !== "Cancelled" && (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleCancelSale(sale)}
                    >
                      Cancel
                    </Button>
                  )}
              </Stack>
            ),
          },
        ]}
      />

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>New Uniform Sale</DialogTitle>
        <DialogContent>
          {step === 1 && (
            <>
              <Autocomplete
                sx={{ mt: 1 }}
                options={studentOptions}
                getOptionLabel={(s) => `${s.name} (${s.studentId})`}
                value={selectedStudent}
                onChange={(e, v) => {
                  setSelectedStudent(v);
                  if (v) {
                    setForm((f) => ({
                      ...f,
                      studentId: v.studentId,
                      studentName: v.name,
                      grade: v.grade,
                      program: v.program,
                    }));
                  }
                }}
                inputValue={studentInput}
                onInputChange={(e, v) => setStudentInput(v)}
                renderInput={(params) => (
                  <TextField {...params} label="Search student" />
                )}
              />

              {selectedStudent && (
                <>
                  <Stack direction="row" spacing={2} sx={{ my: 2 }}>
                    <Typography variant="body2">
                      Grade: {selectedStudent.grade}
                    </Typography>
                    <Typography variant="body2">
                      Program: {selectedStudent.program}
                    </Typography>
                  </Stack>

                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" gutterBottom>
                    Pick uniforms
                  </Typography>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <FormControl fullWidth>
                      <InputLabel>Gender</InputLabel>
                      <Select
                        value={picker.gender}
                        label="Gender"
                        onChange={(e) =>
                          handlePickerChange("gender", e.target.value)
                        }
                      >
                        {GENDERS.map((g) => (
                          <MenuItem key={g.value} value={g.value}>
                            {g.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth disabled={!picker.gender}>
                      <InputLabel>Product</InputLabel>
                      <Select
                        value={
                          typeof picker.uniformGroup === "string"
                            ? picker.uniformGroup
                            : ""
                        }
                        label="Product"
                        onChange={(e) =>
                          handlePickerChange("uniformGroup", e.target.value)
                        }
                        renderValue={(v) => safeDisplay(v)}
                      >
                        {onlyNonEmptyStrings(filterOptions.groups).map((g) => (
                          <MenuItem key={g} value={g}>
                            {g}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth disabled={!picker.uniformGroup}>
                      <InputLabel>Variant</InputLabel>
                      <Select
                        value={
                          typeof picker.uniformVariant === "string"
                            ? picker.uniformVariant
                            : ""
                        }
                        label="Variant"
                        onChange={(e) =>
                          handlePickerChange("uniformVariant", e.target.value)
                        }
                        renderValue={(v) => variantLabel(v)}
                      >
                        {onlyNonEmptyStrings(filterOptions.variants).map((v) => (
                          <MenuItem key={v} value={v}>
                            {variantLabel(v)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>

                  {picker.uniformGroup && picker.uniformVariant && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Select size
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Size</TableCell>
                            <TableCell align="right">Price</TableCell>
                            <TableCell align="right">Stock</TableCell>
                            <TableCell align="right">Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sizeGridRows.map((sku) => (
                            <TableRow key={sku._id}>
                              <TableCell>{safeDisplay(sku.size) || "-"}</TableCell>
                              <TableCell align="right">
                                ₹{safeDisplay(sku.price) || "0"}
                              </TableCell>
                              <TableCell align="right">{getSkuStock(sku)}</TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  onClick={() => addSkuToCart(sku)}
                                  disabled={getSkuStock(sku) <= 0}
                                >
                                  Add
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                          {sizeGridRows.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={4}>
                                No SKUs found for this selection.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </Box>
                  )}

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Cart
                    </Typography>
                    {selectedItems.map((item, idx) => (
                      <Stack
                        key={item.id}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <Chip label={safeDisplay(item.label) || "Item"} />
                        <TextField
                          type="number"
                          size="small"
                          label="Qty"
                          sx={{ width: 90 }}
                          value={item.quantity}
                          inputProps={{ min: 1 }}
                          onChange={(e) => {
                            const quantity = Math.max(
                              1,
                              parseInt(e.target.value, 10) || 1
                            );
                            setSelectedItems((list) =>
                              list.map((it, i) =>
                                i === idx ? { ...it, quantity } : it
                              )
                            );
                          }}
                        />
                        <Typography variant="body2">
                          Stock: {toNumber(item.stock)}
                        </Typography>
                        <Button
                          color="error"
                          size="small"
                          onClick={() =>
                            setSelectedItems((list) =>
                              list.filter((_, i) => i !== idx)
                            )
                          }
                        >
                          Remove
                        </Button>
                      </Stack>
                    ))}
                    {!selectedItems.length && (
                      <Typography variant="body2" color="text.secondary">
                        No items added yet.
                      </Typography>
                    )}
                  </Box>
                </>
              )}
            </>
          )}

          {step === 2 && (
            <>
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{safeDisplay(item.label) || "Item"}</TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="right">
                        ₹{(item.quantity * item.price).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={2} align="right">
                      <strong>Grand total</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>
                        ₹{getTotalAmount(selectedItems).toLocaleString()}
                      </strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              <TextField
                label="Manual receipt no (optional)"
                fullWidth
                sx={{ mt: 2 }}
                value={form.manualReceiptNumber}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    manualReceiptNumber: e.target.value,
                  }))
                }
              />

              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Split payment?</InputLabel>
                <Select
                  value={splitPayment ? "yes" : "no"}
                  label="Split payment?"
                  onChange={(e) => setSplitPayment(e.target.value === "yes")}
                >
                  <MenuItem value="no">Single payment</MenuItem>
                  <MenuItem value="yes">Split payment</MenuItem>
                </Select>
              </FormControl>

              {!splitPayment ? (
                <>
                  <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel>Payment mode</InputLabel>
                    <Select
                      value={form.paymentMode}
                      label="Payment mode"
                      onChange={(e) =>
                        setForm((f) => ({ ...f, paymentMode: e.target.value }))
                      }
                    >
                      {paymentModes.map((mode) => (
                        <MenuItem key={mode} value={mode}>
                          {mode}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Payment reference"
                    fullWidth
                    sx={{ mt: 2 }}
                    value={form.paymentInfo}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, paymentInfo: e.target.value }))
                    }
                  />
                </>
              ) : (
                <Box sx={{ mt: 2 }}>
                  {paymentSplits.map((p, idx) => (
                    <Stack key={idx} direction="row" spacing={1} sx={{ mb: 1 }}>
                      <FormControl sx={{ minWidth: 140 }}>
                        <InputLabel>Mode</InputLabel>
                        <Select
                          value={p.mode}
                          label="Mode"
                          onChange={(e) => {
                            const next = [...paymentSplits];
                            next[idx].mode = e.target.value;
                            setPaymentSplits(next);
                          }}
                        >
                          {paymentModes.map((mode) => (
                            <MenuItem key={mode} value={mode}>
                              {mode}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        label="Amount"
                        type="number"
                        value={p.amount}
                        onChange={(e) => {
                          const next = [...paymentSplits];
                          next[idx].amount = e.target.value;
                          setPaymentSplits(next);
                        }}
                      />
                      <TextField
                        label="Ref"
                        value={p.info}
                        onChange={(e) => {
                          const next = [...paymentSplits];
                          next[idx].info = e.target.value;
                          setPaymentSplits(next);
                        }}
                      />
                    </Stack>
                  ))}
                  <Button
                    size="small"
                    onClick={() =>
                      setPaymentSplits([
                        ...paymentSplits,
                        { mode: "", amount: "", info: "" },
                      ])
                    }
                  >
                    + Add payment line
                  </Button>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          {step === 1 && (
            <Button
              variant="contained"
              disabled={!selectedStudent || selectedItems.length === 0}
              onClick={() => setStep(2)}
            >
              Next
            </Button>
          )}
          {step === 2 && (
            <>
              <Button onClick={() => setStep(1)}>Back</Button>
              <Button variant="contained" onClick={handleSave}>
                Received Amount
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {deliverSale && (
        <PartialDeliveryDialog
          open={deliverDialogOpen}
          onClose={() => {
            setDeliverDialogOpen(false);
            setDeliverSale(null);
          }}
          sale={deliverSale}
          stockMap={stockMap}
          onDeliver={handleDeliver}
        />
      )}

      <Dialog
        open={receiptDialogOpen}
        onClose={() => setReceiptDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Receipt</DialogTitle>
        <DialogContent>
          {receiptSale && (
            <SaleReceipt
              ref={receiptRef}
              sale={receiptSale}
              branches={branches}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiptDialogOpen(false)}>Close</Button>
          <Button variant="contained" onClick={handlePrint}>
            Print
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UniformSales;
